import clsx from 'clsx'
import update from 'immutability-helper'
import {pick} from 'lodash'
import React, {useState, useMemo} from 'react'
import {useHistory} from 'react-router-dom'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'

import CheckBox from '@mui/material/Checkbox'
import InputLabel from '@mui/material/InputLabel'
import FormControl from '@mui/material/FormControl'
import FormHelperText from '@mui/material/FormHelperText'

import {makeStyles} from 'external/material'

import useForm from 'helpers/form-hook'
import useValidations from 'helpers/validations-hook'
import {createPost} from 'helpers/command'

import Dialog from 'components/responsive-fullscreen-dialog'
import TextInput from './TextInput'
import Select from './Select'
import SelectDirectory from '../../components/SelectDirectory'
import validations from './validations'

import MainTopics from './generator/MainTopics'

const initialState = {
  attempted: false,
  type: '',
  title: '',
  subject: '',
  course: '',
  directory: '',
  unit: '',
  useGenerator: false,
  topics: []
}

const inDevelopment = process.env.NODE_ENV === 'development';

const useStyles = makeStyles(createStyles)

export default function CreateAssessmentForm ({context, useCart}) {

  const history = useHistory()

  const isTesting = (context.profile.domain == 'testing-gen') || inDevelopment;

  const {translate, quizDialogs, myCourses} = context

  const isOpen = quizDialogs['create-assessment']

  const classes = useStyles()
  const {state, setState, handleChange, resetState} = useForm(initialState)
  const {errors, errorCount, validate, resetErrors} = useValidations(validations)


  const createStateHandler = mapState => (event) => {
    const value = event.target.value == '' ? undefined : event.target.value
    setState({...state, ...mapState(value)})
  }

  const courses = state.subject
    ? myCourses.filter(({course}) => course.substring(0,2) == state.subject)
    : myCourses

  function closeDialog () {
    const quizDialogs = update(context.quizDialogs, {'create-assessment': {$set: false}})
    context.setState({quizDialogs})
    resetState()
    resetErrors()
  }

  async function createAssessment() {

    if (validate({...state, attempted: true})) {

      const cart = useCart ? context.quizQuestionBankCart.map(q => q.id) : undefined
      const topics = state.useGenerator ? state.topics : undefined

      const assessment = pick(state, ['title', 'type', 'subject', 'course', 'unit', 'directory'])
      if (state.directory == '') {
        assessment.directory = undefined;
      }
      try {
        const [created] = await createPost('quiz', topics ? 'generateAssessmentWithTopics' : 'createAssessment')({...assessment, cart, topics})
        if (created) {
          context.setState({quizAssessmentCalls: context.quizAssessmentCalls + 1})
          history.push(context.link({assessment: created.id, sectionId: created.firstSection}, '/:languageId/:role/:page/:assessment/edit/:sectionId'))
        }
        else {
          setState({...state, attempted: true})
        }
      } catch (e) {
      }
      closeDialog()
    }
    else {
      setState({...state, attempted: true})
    }
  }

  React.useEffect(() => { validate(state) }, [state])

  React.useEffect(() => {

    if (state.directory != context.quizSelectedDirectory)
      setState({...state, directory: context.quizSelectedDirectory})

  }, [context.quizSelectedDirectory])

  React.useEffect(() => {
    if (state.useGenerator) {
      setState({...state, topics: Object.entries(context.quizGenerator.selectedTopics).map(([topic, {questionCount}]) => ({topic, questionCount}))})
    } else {
      setState({...state, topics: []})
    }
  }, [state.useGenerator, context.quizGenerator.selectedTopics])
  
  const topicError = useMemo(() => {
    return errors.topics == '' ? undefined : true
  }, [errors.topics])

  return (
    <Dialog
      open={isOpen}
      close={closeDialog}
      actionsClassName={classes.actions}
      confirm={
        <Button
          variant="contained"
          className={clsx(classes.button, "create")}
          onClick={createAssessment}>
            {translate('create')}
        </Button>
      }
      cancel={
        <Button
          variant="contained"
          className={clsx(classes.button, "cancel")}
          onClick={closeDialog}>
            {translate('cancel')}
        </Button>
      }
      title={translate('create-assessment-dialog-title')}
    >
        <Box className={classes.root}>
          <Box className={clsx(classes.field, "title")}>
           <TextInput
             controlFullWidth
             controlVariant="outlined"
             controlMargin="dense"
             id="title"
             inputAutoComplete="title"
             labelRequired
             inputAutoFocus
             caption={translate('title')}
             value={state.title}
             inputOnChange={handleChange('title')}
             controlError={errors.title.length > 0}
             errorMessage={errors.title.length > 0 ? translate(errors.title) : undefined}
           />
          </Box>
          <Box className={clsx(classes.field, "type")}>
            <Select
              controlFullWidth
              controlVariant="outlined"
              controlMargin="dense"
              id="type"
              labelRequired
              caption={translate('type_label')}
              selectValue={state.type}
              selectOnChange={handleChange('type', e => parseInt(e.target.value))}
              controlError={errors.type.length > 0}
              errorMessage={errors.type.length > 0 ? translate(errors.type): undefined}
              mapValue={o => o.id}
              mapText={o => translate(`${o.name}-label`)}
              options={context.quizTypes}
            />
          </Box>
          <Box className={clsx(classes.field, "subject")}>
            <Select
              controlFullWidth
              controlVariant="outlined"
              controlMargin="dense"
              selectValue={state.subject}
              selectOnChange={createStateHandler(subject => {
                return {
                  subject,
                  subject_touched: true,
                  course: '',
                  course_touched: false
                }
              })}
              caption={translate('subject_label')}
              controlError={errors.subject.length > 0}
              errorMessage={errors.subject.length > 0 ? translate(errors.subject): undefined}
              mapValue={o => o.id}
              mapText={o => o.name}
              options={context.mySubjects}
              />
          </Box>
          <Box className={clsx(classes.field, "course")}>
            <Select
              controlFullWidth
              controlVariant="outlined"
              controlMargin="dense"
              selectValue={state.course}
              selectOnChange={createStateHandler(course => {
                return {
                  course,
                  course_touched: true,
                  subject: course.substring(0,2),
                  subject_touched: true
                }
              })}
              caption={translate('course_assessment_header')}
              controlError={errors.course.length > 0}
              errorMessage={errors.course.length > 0 ? translate(errors.course): undefined}
              mapValue={o => o.course}
              mapText={o => o.courseName}
              options={courses}
            />
          </Box>
          <Box className={clsx(classes.field, "unit")}>
            <TextInput
              controlFullWidth
              controlVariant="outlined"
              controlMargin="dense"
              id="unit"
              inputAutoComplete="unit"
              caption={translate('unit')}
              value={state.unit}
              inputOnChange={handleChange('unit')}
            />
          </Box>
          <Box className={clsx(classes.field, "directory")}>
            <SelectDirectory
              controlFullWidth
              controlVariant="outlined"
              id="directory"
              caption={translate('directory')}
              selectValue={state.directory || ''}
              selectOnChange={handleChange('directory')}
              context={context}
            />
          </Box>
          {isTesting && (
            <Box className={clsx(classes.field, "generator-question")}>
              <FormControl
                error={topicError}
              >
                <InputLabel shrink={false} disableAnimation={true} htmlFor="use-generator-checkbox">{translate("quiz-generate-by-topics-label")}</InputLabel>
                <CheckBox disableRipple={true} aria-describedby="topic-error-text" id="use-generator-checkbox" onChange={handleChange('useGenerator', () => !state.useGenerator)} checked={state.useGenerator} />
                <FormHelperText error={topicError} id="topic-error-text" >{translate(errors.topics)}</FormHelperText>
              </FormControl>
            </Box>
          )}
          {state.useGenerator && (
            <Box className={clsx(classes.field, "generator")}>
              <MainTopics />
            </Box>
          )}
        </Box>
    </Dialog>
  )
}

function createStyles(theme) {

  const verticalCentering = {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center'
  }

  return {
    root: {
      display: 'flex',
      flexDirection: 'column',
    },
    field: {
      flexGrow: 1,
      display: 'flex',
      marginBottom: 25,
      minWidth: 500,
      "& > div": {
        display: 'flex',
        flexGrow: 1,
        "& .label": {
          width: 90,
          fontSize: '12pt',
          fontWeight: 'bold',
          color: '#878ea1',
          ...verticalCentering
        },
        "& .input": {
          flexGrow: 1,
          ...verticalCentering,
          "& input": {
            borderRadius: 4,
            border: 'solid 1px #aab7bf !important',
          },
          "& .select": {
            "& .MuiInput-underline:before": {
              content: 'none',
              border: 'none'
            },
            "& .MuiSelect-select": {
              border: 'solid 1px #aab7bf',
              borderRadius: 4,
              padding: 10,
              paddingRight: 24
            },
          }
        }
      }
    },
    button: {
      width: 120,
      fontSize: "13pt",
      textTransform: "none",
      boxShadow: 'none',
      borderRadius: 4,
      "&:first-child": {
        marginRight: 10
      },
      "&:last-child": {
        marginLeft: 10
      },
      "&.cancel": {
        border: "solid 1px #3d4556",
        backgroundColor: '#fff',
        color: "#3d4556",
        '&:hover': {
          color: "#000",
          border: "solid 1px #000",
          backgroundColor: '#eaf5ff',
        },
      },
      "&.create" : {
        color: "#fff",
        border: 'solid 1px #1d9be5',
        backgroundColor: '#1d9be5',
        color: '#fff',
        '&:hover': {
          backgroundColor: '#29739f',
          borderColor: '#29739f',
          boxShadow: 'none',
        },
      }
    },
    actions: {
      padding: '8px 24px 30px 24px',
    }
  }
}

